// App.jsx - The route tree. The customer site is loaded eagerly because it is
// what almost every visitor came for; the dashboard is a lazy chunk so a customer
// never downloads it.
//
// ScrollToTop is here rather than in a layout because it has to run for every
// route change including the ones inside the dashboard.

import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation, useParams, Navigate } from "react-router";

import { isAdminHost } from "./lib/host.js";

import SiteLayout from "./site/SiteLayout.jsx";
import Home from "./site/Home.jsx";
import HowItWorks from "./site/HowItWorks.jsx";
import WhatWeBuy from "./site/WhatWeBuy.jsx";
import WhyUs from "./site/WhyUs.jsx";
import SellScrapGold from "./site/SellScrapGold.jsx";
import RequestKit from "./site/RequestKit.jsx";
import Prices from "./site/Prices.jsx";
import Embed from "./site/Embed.jsx";
import PriceAlerts from "./site/StayUpdated.jsx";
import Tools from "./site/Tools.jsx";
import Calculator from "./site/Calculator.jsx";
import KaratChart from "./site/KaratChart.jsx";
import WeightConverter from "./site/WeightConverter.jsx";
import Learn from "./site/Learn.jsx";
import Article from "./site/Article.jsx";
import Faq from "./site/Faq.jsx";
import About from "./site/About.jsx";
import Contact from "./site/Contact.jsx";
import WorkWithUs from "./site/WorkWithUs.jsx";
import Coverage from "./site/Coverage.jsx";
import Shows, { Show } from "./site/Shows.jsx";
import KitConfirmed from "./site/KitConfirmed.jsx";
import Legal, { LegalIndex } from "./site/Legal.jsx";
import AffiliateSignup from "./site/AffiliateSignup.jsx";
import AffiliatePortal from "./site/AffiliatePortal.jsx";
import TrackFind from "./site/TrackFind.jsx";
import Track from "./site/Track.jsx";
import NotFound from "./site/NotFound.jsx";

import { Loading } from "./ui/kit.jsx";
import { rememberReferral, rememberCard } from "./lib/referral.js";

const AdminApp = lazy(() => import("./admin/AdminApp.jsx"));

function WidgetRedirect() {
  const { search, hash } = useLocation();
  useEffect(() => {
    window.location.replace(`/widget.html${search}${hash}`);
  }, [search, hash]);
  return <Loading label="Opening the widget" className="min-h-screen" />;
}

// An affiliate's link. Short enough to print on a card, and it does exactly one
// thing: remember who sent them, then get out of the way. There is no landing
// page, no "referred by" banner and no mention of it anywhere the customer can
// see, because a referral is our arrangement with the affiliate and not
// something the customer is part of.
//
// `replace` rather than a push, so the back button goes where they came from
// rather than bouncing them through here again.
function AffiliateLink() {
  const { code } = useParams();
  useEffect(() => { rememberReferral(`/a/${code}`); }, [code]);
  return <Navigate to="/get-a-kit" replace />;
}

// The QR code on the back of a counter card. One segment, CODE-SERIAL, because
// every character in that URL is another module in a grid somebody has to scan
// off a card in a shop with bad lighting.
//
// It goes somewhere different from an affiliate link on purpose. This person is
// holding the mailer already: sending them to the ordinary form would promise to
// post them one, and we would pay to send a second mailer to somebody whose gold
// is about to be in the post.
function CardLink() {
  const { token } = useParams();
  const [code, serial] = String(token || "").split("-");

  useEffect(() => {
    rememberReferral(`/a/${code}`);
    // A card with no serial still works. It is one printed before serials, or
    // one somebody typed the short half of, and the order is worth more than the
    // matching.
    if (serial) rememberCard(serial);
  }, [code, serial]);

  return <Navigate to="/have-a-mailer" replace />;
}

function ScrollToTop() {
  const { pathname, hash, search } = useLocation();

  // Any page, not just the short link. An affiliate pasting ?ref=CODE onto a
  // guide is as much a referral as one handing over a card.
  useEffect(() => { rememberReferral(pathname, search); }, [pathname, search]);

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

const dashboard = (
  <Suspense fallback={<Loading label="Opening the dashboard" className="min-h-screen" />}>
    <AdminApp />
  </Suspense>
);

export default function App() {
  // One build, two hostnames. On admin.* the customer site does not exist at all,
  // so a stray path lands on the dashboard rather than leaking the marketing site
  // onto the staff subdomain. index.html has already redirected on first load;
  // this covers client side navigation and anything that bypassed the script.
  if (isAdminHost()) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/admin/*" element={dashboard} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* The dashboard stays reachable at /admin on the main hostname too, which
            is how it is used in development. */}
        <Route path="/admin/*" element={dashboard} />

        {/* The embeddable widget is a static file, not a route, so arriving here
            means somebody wrote /widget or /widget/ instead of /widget.html.
            Bounce them rather than showing a not found page: these urls get
            copied into other people's html by hand and by assistants, and one
            that only works with a trailing slash is one that will be got wrong.
            Outside SiteLayout, so a redirect does not flash a header, and a full
            page load rather than a navigation, because the target is a file the
            app must not try to handle. */}
        <Route path="/widget/*" element={<WidgetRedirect />} />

        {/* Customer site */}
        <Route element={<SiteLayout />}>
          <Route index element={<Home />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="what-we-buy" element={<WhatWeBuy />} />
          <Route path="why-us" element={<WhyUs />} />
          <Route path="sell-scrap-gold" element={<SellScrapGold />} />
          <Route path="get-a-kit" element={<RequestKit />} />
          {/* The same form for somebody already holding a mailer. Its own
              address rather than a query string, so the copy on it is the
              copy a crawler and a returning visitor both get. */}
          <Route path="have-a-mailer" element={<RequestKit mailer />} />
          <Route path="prices" element={<Prices />} />
          <Route path="embed" element={<Embed />} />
          <Route path="price-alerts" element={<PriceAlerts />} />

          <Route path="tools" element={<Tools />} />
          <Route path="tools/gold-calculator" element={<Calculator metal="gold" />} />
          <Route path="tools/silver-calculator" element={<Calculator metal="silver" />} />
          <Route path="tools/platinum-calculator" element={<Calculator metal="platinum" />} />
          <Route path="tools/karat-chart" element={<KaratChart />} />
          <Route path="tools/weight-converter" element={<WeightConverter />} />

          <Route path="learn" element={<Learn />} />
          <Route path="learn/:slug" element={<Article />} />

          <Route path="faq" element={<Faq />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="work-with-us" element={<WorkWithUs />} />
          {/* The two names people guess for the same page. */}
          <Route path="affiliates" element={<Navigate to="/work-with-us" replace />} />
          <Route path="affiliate" element={<Navigate to="/work-with-us" replace />} />

          <Route path="extra-coverage" element={<Coverage />} />

          <Route path="get-a-kit/confirmed" element={<KitConfirmed />} />

          <Route path="shows" element={<Shows />} />
          <Route path="shows/:slug" element={<Show />} />

          <Route path="track" element={<TrackFind />} />
          <Route path="track/:token" element={<Track />} />

          <Route path="legal" element={<LegalIndex />} />
          <Route path="legal/terms" element={<Legal doc="terms" />} />
          <Route path="legal/privacy" element={<Legal doc="privacy" />} />
          <Route path="legal/shipping" element={<Legal doc="shipping" />} />
          <Route path="legal/purchase-agreement" element={<Legal doc="purchase-agreement" />} />
          <Route path="legal/aml" element={<Legal doc="aml" />} />
          <Route path="legal/sms" element={<Legal doc="sms" />} />
          <Route path="legal/accessibility" element={<Legal doc="accessibility" />} />
          {/* Not linked from anywhere a customer looks. Given to affiliates. */}
          <Route path="legal/affiliate-terms" element={<Legal doc="affiliate-terms" />} />

          {/* Old links people may still have. */}
          <Route path="blog" element={<Navigate to="/learn" replace />} />
          {/* The short link in every price alert text. A tagged url is 108
              characters and a text is 160, so the tags are stamped on arrival
              here instead of being carried in the message. See the sms fields
              in server/src/lib/templates.js. */}
          <Route path="start" element={<Navigate to="/get-a-kit?utm_source=price_alert&utm_medium=message" replace />} />
          {/* An affiliate's own link. Nothing renders here. */}
          <Route path="a/:code" element={<AffiliateLink />} />
          {/* The QR on a counter card. Nothing renders here either. */}
          <Route path="m/:token" element={<CardLink />} />

          {/* Sent to somebody, never linked. Not in the nav, not in the footer,
              not in the sitemap, and noindex on the page itself. */}
          <Route path="affiliate-signup" element={<AffiliateSignup />} />
          {/* Their own page: what they sent us and what it earned. Public, but
              noindex, because a search result for it is a stranger landing on
              somebody else's earnings screen. */}
          <Route path="affiliate-portal" element={<AffiliatePortal />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}
